import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { useApp, formatINR, optimizedImage, imageSrcSet, handleImgError, minVariantPrice } from "../store";
import useDocumentMeta from "../hooks/useDocumentMeta";

const HERO_IMG = "https://images.unsplash.com/photo-1747847386065-a2dbc7db1ebc";
const ROSE_IMG = "https://images.unsplash.com/photo-1764160647304-6ab4dcd82d63";
const JASMINE_IMG = "https://images.unsplash.com/photo-1623171404570-1d196759fe20";
const SANDAL_IMG = "https://images.unsplash.com/photo-1702001145743-094b0b7ead51";
const BOTTLE_IMG = "https://images.unsplash.com/photo-1781344116880-029945a42b75";

export default function Home() {
  useDocumentMeta({
    title: null, // uses default site title
    description: "Buy pure floral absolutes online — Ruh Gulab (Rose Absolute), Jasmine Sambac Absolute, Jasmine Grandiflorum Absolute, Tuberose Absolute, Pink Lotus Absolute, Patchouli Oil, Nagarmotha Oil. 100% natural, GC/MS verified, distilled in Kannauj, worldwide shipping.",
    image: "https://customer-assets.emergentagent.com/job_fragrance-essentials/artifacts/br1bdgvw_image.png",
    keywords: [
      "rose absolute", "jasmine absolute", "tuberose absolute", "pink lotus absolute",
      "lotus absolute", "buy rose absolute", "buy jasmine absolute",
      "jasmine sambac absolute", "jasmine grandiflorum absolute", "rose absolute oil",
      "natural tuberose absolute", "pure floral absolute online", "Patchouli Oil", "Nagarmotha Oil",
    ],
  });
  const { API, settings } = useApp();
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then(r => setFeatured(r.data.slice(0, 6)));
  }, [API]);
  const s = settings || {};

  return (
    <div className="bg-cream" data-testid="home-page">
      {/* HERO */}
      <section className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <div className="overline text-copper mb-8">{s.hero_eyebrow || "Est. Kannauj · India · Since The Mughal Era"}</div>
            <h1 className="headline-xl text-[clamp(3.5rem,9vw,9.5rem)] mb-10">
              {s.hero_headline_top || "The Scent"}<br/>
              <span className="italic text-copper">{s.hero_headline_bottom || "of First Rain."}</span>
            </h1>
            <p className="text-base md:text-lg text-[#5C5E5C] max-w-xl leading-relaxed font-light mb-10">
              {s.hero_subheadline || "Twenty rare distillations — Rose, Oud, Mitti, Ruh Khus — pressed by copper deg-bhapka and solvent absolutes from the soil that once perfumed emperors. No carriers. No compromise."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalog" className="btn-primary" data-testid="hero-shop-cta">
                Explore the catalog <ArrowRight size={14} />
              </Link>
              <Link to="/inquiry" className="btn-ghost" data-testid="hero-rfq-cta">B2B / Bulk inquiry</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative h-[520px] overflow-hidden bg-clay">
              {s.hero_video_url ? (
                <video
                  src={s.hero_video_url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={s.hero_image_url ? optimizedImage(s.hero_image_url, 1200) : undefined}
                  className="w-full h-full object-cover"
                  data-testid="hero-video"
                />
              ) : s.hero_image_url ? (
                <img
                  src={optimizedImage(s.hero_image_url, 1200)}
                  srcSet={imageSrcSet(s.hero_image_url, [600, 900, 1200], 78)}
                  sizes="(max-width: 1024px) 100vw, 42vw"
                  alt="House of Absolutes — Kannauj deg-bhapka distillation"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  width="1200"
                  height="520"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EBE4D8] via-[#F3EFE9] to-[#FAF9F6]" data-testid="hero-empty-placeholder">
                  <div className="text-center px-8">
                    <div className="overline text-[10px] text-copper mb-2">House Notes</div>
                    <p className="font-serif-display text-2xl italic text-[#1E1F1E]/70 leading-tight">Kannauj · Since the Mughal Era</p>
                  </div>
                </div>
              )}
              <div className="absolute bottom-6 left-6 right-6 bg-[#FAF9F6]/90 backdrop-blur px-6 py-4">
                <div className="overline text-[10px] text-copper mb-1">N° 09 · House Notes</div>
                <p className="font-serif-display text-lg leading-snug">{s.hero_quote || '"Petrichor on baked clay. Sandalwood awaits the monsoon."'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial ribbon marquee */}
      {(s.show_marquee !== false) && (
      <section className="bg-sand py-12 border-y border-[#E5E1D8] marquee-wrap">
        <div className="marquee-track">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="inline-flex gap-24 items-center">
              {["Ruh Gulab", "Jasmine Sambac", "Mitti Attar", "Agarwood Oud", "Pink Lotus", "Ruh Khus", "Champaca", "Sandalwood", "Tuberose"].map((n, i) => (
                <span key={`${dup}-${i}`} className="font-serif-display text-5xl md:text-7xl italic text-[#1E1F1E]/80 inline-flex items-center gap-12">
                  {n}<span className="text-copper not-italic">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Categories grid */}
      {(s.show_categories !== false) && (
      <section className="py-32 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-4">
            <div className="overline text-copper mb-4">The Five Houses</div>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-none">Categories<br/><span className="italic text-[#8A8D8A]">of essence</span></h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 text-[#5C5E5C] leading-relaxed self-end">
            From velvet-soft florals to smoke-edged woods, each category is built around a single principle: extract honestly, distill patiently, and let the plant speak.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-1">
          <CategoryTile name="Floral & Absolute" subtitle={s.cat_floral_subtitle || "Velvet petals · Solvent extracted"} img={s.cat_floral_image || JASMINE_IMG} testid="cat-floral" />
          <CategoryTile name="Attar" subtitle={s.cat_attar_subtitle || "Copper-still · Kannauj heritage"} img={s.cat_attar_image || HERO_IMG} testid="cat-attar" />
          <CategoryTile name="Wood & Resin" subtitle={s.cat_wood_subtitle || "Heartwood · Patient depth"} img={s.cat_wood_image || SANDAL_IMG} testid="cat-wood" />
          <CategoryTile name="Herb & Grass" subtitle={s.cat_herb_subtitle || "Bright · Green · Lifted"} img={s.cat_herb_image || BOTTLE_IMG} testid="cat-herb" />
        </div>
      </section>
      )}

      {/* Featured products */}
      {(s.show_featured !== false) && (
      <section className="py-32 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <div className="overline text-copper mb-4">House Selection</div>
            <h2 className="font-serif-display text-5xl md:text-6xl">Distillations of note</h2>
          </div>
          <Link to="/catalog" className="overline text-xs hover:text-copper flex items-center gap-2" data-testid="featured-view-all">
            View all twenty <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
          {featured.map((p, i) => (
            <Link key={p.id} to={`/product/${p.id}`} className="product-tile group" data-testid={`featured-${p.id}`}>
              <div className="aspect-[4/5] overflow-hidden bg-clay mb-5">
                <img
                  src={optimizedImage(p.image_url, 700, 65)}
                  srcSet={imageSrcSet(p.image_url, [400, 600, 900], 65)}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt={`Buy ${p.name}${p.botanical_name ? ` (${p.botanical_name})` : ''} online — 100% pure natural from Kannauj`}
                  loading={i < 3 ? "eager" : "lazy"}
                  fetchpriority={i < 3 ? "high" : "auto"}
                  decoding="async"
                  width="700"
                  height="875"
                  onError={handleImgError(p)}
                  className="product-img w-full h-full object-cover"
                />
              </div>
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="overline text-[10px] text-[#8A8D8A] mb-1">{p.category}</div>
                  <h3 className="font-serif-display text-2xl leading-tight">{p.name}</h3>
                  <p className="text-xs text-[#8A8D8A] italic mt-1">{p.botanical_name}</p>
                </div>
                <div className="text-right">
                  <div className="overline text-[10px] text-[#8A8D8A]">From</div>
                  <div className="font-serif-display text-xl">{formatINR(minVariantPrice(p))}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* Story block */}
      {(s.show_story !== false) && (
      <section className="bg-[#1E1F1E] text-[#FAF9F6] py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={optimizedImage(s.story_image_url || ROSE_IMG, 800, 68)}
              srcSet={imageSrcSet(s.story_image_url || ROSE_IMG, [400, 600, 900], 68)}
              sizes="(max-width: 1024px) 100vw, 42vw"
              alt="Story"
              loading="lazy"
              decoding="async"
              width="800"
              height="600"
              className="w-full h-[600px] object-cover"
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="overline text-copper mb-6">{s.story_eyebrow || "The Method"}</div>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-tight mb-10">
              {s.story_headline_top || "Copper, fire,"}<br/>
              <span className="italic">{s.story_headline_bottom || "forty kilograms of rose."}</span>
            </h2>
            <p className="text-[#8A8D8A] leading-relaxed mb-6 font-light text-lg">
              {s.story_paragraph_1 || "In Kannauj, our distillers carry techniques forward from the 16th century. Deg-bhapka — the copper still — coaxes the soul out of the petal, the root, the resin. Forty kilos of Rosa damascena yields a single tola of attar. Patience, in liquid form."}
            </p>
            <p className="text-[#8A8D8A] leading-relaxed font-light text-lg">
              {s.story_paragraph_2 || "Every batch carries a CAS number, an origin certificate, and the maker's name. International buyers receive COA documents and CITES permits where applicable."}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* B2B CTA */}
      {(s.show_b2b_cta !== false) && (
      <section className="py-32 px-6 md:px-12 max-w-[1500px] mx-auto text-center">
        <div className="overline text-copper mb-6">For Houses, Brands & Perfumers</div>
        <h2 className="font-serif-display text-6xl md:text-8xl leading-none mb-10">
          Bulk inquiries<br/>
          <span className="italic text-[#8A8D8A]">welcomed.</span>
        </h2>
        <p className="text-[#5C5E5C] max-w-2xl mx-auto mb-10 leading-relaxed">
          1kg+ pack sizes, private-label distillations, and custom blends for indie perfumers, soap-makers, and luxury brands worldwide.
        </p>
        <Link to="/inquiry" className="btn-primary" data-testid="bottom-rfq-cta">Request a quote</Link>
      </section>
      )}
    </div>
  );
}

function CategoryTile({ name, subtitle, img, testid }) {
  return (
    <Link to={`/catalog?category=${encodeURIComponent(name)}`} className="product-tile group relative aspect-[3/4] overflow-hidden block" data-testid={testid}>
      <img src={optimizedImage(img, 600)} alt={name} loading="lazy" decoding="async" className="product-img w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6 text-[#FAF9F6]">
        <div className="overline text-[10px] text-[#EBE4D8] mb-1">{subtitle}</div>
        <h3 className="font-serif-display text-3xl">{name}</h3>
      </div>
    </Link>
  );
}
